import { css } from 'lit';
import { APIConfig } from '@/config/ConfigCARSx';
import { ConfigREMApi } from '@/config/ConfigREM';
import APIRequest, { getAPIHeaders } from '@/redux/APIRequest';
import { centroid, nearestPointOnLine, polygonToLine } from '@turf/turf';
import { AreaDto, LocationDto, RoadEventDto, RouteDto } from '@typings/api';
import { Feature, Geometry, LineString, MultiLineString } from 'geojson';
import HTTPMethod from 'http-method-enum';
import { parse } from 'wellknown';

export const getRouteGeometry = async (route: string) => {
	const url = new URL(ConfigREMApi.codedRoadway(route), APIConfig.endpointURLBase);

	const apiRequestReturn = await APIRequest(
		new Request(url.href, {
			method: HTTPMethod.GET,
			headers: new Headers({
				...getAPIHeaders(),
			}),
		}),
	);

	if (apiRequestReturn.response?.ok) {
		const geometry = ((await apiRequestReturn.response.json()) as RouteDto)
			.geometry as unknown as MultiLineString;

		return geometry;
	}

	return null;
};

export const getRoutes = async () => {
	const apiRequestReturn = await APIRequest(
		new Request(new URL(ConfigREMApi.routesByCountyDistrict(), APIConfig.endpointURLBase).href, {
			method: HTTPMethod.GET,
			headers: new Headers({
				...getAPIHeaders(),
			}),
		}),
	);

	if (apiRequestReturn.response?.ok) {
		return (await apiRequestReturn.response.json()) as RouteDto[];
	}

	return [];
};

export const getAreas = async (areaType = 'COUNTY'): Promise<AreaDto[]> => {
	const apiRequestReturn = await APIRequest(
		new Request(new URL(ConfigREMApi.areas(areaType), APIConfig.endpointURLBase).href, {
			method: HTTPMethod.GET,
			headers: new Headers({
				...getAPIHeaders(),
			}),
		}),
	);

	if (apiRequestReturn.response?.ok) {
		return (await apiRequestReturn.response.json()) as AreaDto[];
	}

	console.warn(`Failed to get areas of type: ${areaType}`);

	return [];
};

export const getLocationDetailsAtRoutePoint = async (route: string, mile: number) => {
	const url = new URL(ConfigREMApi.countyDistrict(route), APIConfig.endpointURLBase);
	url.searchParams.set('miles_along_route', mile.toString());

	const apiRequestReturn = await APIRequest(
		new Request(url.href, {
			method: HTTPMethod.GET,
			headers: new Headers({
				...getAPIHeaders(),
			}),
		}),
	);

	if (apiRequestReturn.response?.ok) {
		return (await apiRequestReturn.response?.clone().json()) as LocationDto;
	}

	return null;
};

const geometryToLine = (geometry: Geometry) => {
	if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
		return polygonToLine(geometry);
	}
	if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
		return { type: 'Feature', geometry };
	}

	throw new Error(`Unsupported geometry type for snapping: ${geometry.type}`);
};

export const centerEventsOnExtent = (events: RoadEventDto[]): RoadEventDto[] => {
	return events.map((event) => {
		if (event.geometry == null) {
			return event;
		}

		const geometry = parse(event.geometry);

		if (geometry == null || geometry.type === 'Point' || geometry.type === 'GeometryCollection') {
			return event;
		}

		const center = centroid(geometry);
		const snapped = nearestPointOnLine(
			geometryToLine(geometry) as Feature<LineString | MultiLineString>,
			center,
		);
		const [lon, lat] = snapped.geometry.coordinates;
		return { ...event, lat, lon };
	});
};
